/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useRef, useState} from "react";
import {ReservationDriver} from "types/entities/ReservationDriver";
import {RouteComponentProps, useHistory, withRouter} from "react-router-dom";
import BookingLoginReservationInfoCard
    from "pages/Front/Booking/BookingLogin/components/BookingLoginReservationInfoCard";
import {useTranslation} from "react-i18next";
import {useDispatch} from "useDispatch";
import {useTypedSelector} from "helpers/reducers";
import BookingLoginAction from "store/bookingLogin/BookingLoginAction";

import {ReservationDetailContextType} from "types/entities/specific/ReservationDetailContextType";
import {consoleLog} from "utils/utility";
import ReservationDataCollectionInfoCard
    from "pages/Front/Booking/BookingLogin/components/ReservationDataCollectionInfoCard";
import BookingLoginBuyUpsellsCard from "pages/Front/Booking/BookingLogin/components/BookingLoginBuyUpsellsCard";
import BookingAction from "store/booking/BookingAction";
import {Timeslot} from "types/entities/Timeslot";
import {PackageItemItemType} from "types/entities/PackageItem";
import {PackagesCart} from "types/entities/PackagesCart";
import {PackagesCartItem} from "types/entities/PackagesCartItem";
import {Driver} from "types/entities/Driver";
import {ArticleTicketType} from "types/enums/ArticleTicketType";
import BookingLoginZuumeHeader
    from "pages/Front/Booking/BookingLogin/components/BookingLoginReservationDetail/components/BookingLoginZuumeHeader";
import CardComponent from "../components/CardComponent";
import {ExclamationTriangleIcon} from "@heroicons/react/24/outline";
import AccessTicketReservationDriverItem
    from "./components/ReservationDetailComponents/AccessTicketReservationDriverItem";
import FeatherIcon from "components/FeatherIcon";

interface Params {
    reservation_id: string;
}

interface BookingLoginReservationDetailRedesign extends RouteComponentProps<Params> {}

export const BookingLoginReservationDetailDriverRedesignContext = React.createContext<ReservationDetailContextType | null>(null);

const BookingLoginReservationDetailRedesign = (props: BookingLoginReservationDetailRedesign) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    const reservation_id = +props.match.params.reservation_id;
    const bookingProcess = useTypedSelector(state => state.bookingState.bookingProcess);
    const selectedReservation = useTypedSelector(state => state.bookingLoginState.selectedReservation);
    const bookingFilter = useTypedSelector(state => state.bookingState.bookingProcess.filter);
    const drivers: ReservationDriver[] = selectedReservation?.reservationDrivers ? selectedReservation.reservationDrivers : [];
    const profile: Driver | null = useTypedSelector(state => state.bookingLoginState.user);
    const [packagesCart, setPackagesCart] = useState<PackagesCart | null>(null);
    const [accessTicketItems, setAccessTicketItems] = useState<PackagesCartItem[]>([]);
    const [isSessionDriverAssigned, setIsSessionDriverAssigned] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const allAssignedDrivers = drivers.filter(driver => driver.id !== undefined);
    const allGuestsWereAssigned: boolean = selectedReservation?.persons === allAssignedDrivers.length;
    const successMessageRef = useRef<HTMLDivElement>(null);
    const [firstTimeslot, setFirstTimeslot] = useState<Timeslot | null>(null);

    const fetchReservationHandler = () => {
        dispatch(BookingLoginAction.fetchReservationDetail(reservation_id))
            .then(
                onResolve => {
                    setIsLoading(false);
                    setFirstTimeslot(onResolve?.reservation?.firstTimeslot ?? null);
                },
                onReject => {
                    consoleLog(onReject);
                }
            )
            .catch(err => {
                consoleLog(err);
            });
    };

    useEffect(() => {
        const firstDriverIndexWithoutRequiredData = drivers.findIndex(driver => driver.name == "");

        // scroll to success message
        if (allGuestsWereAssigned && firstDriverIndexWithoutRequiredData === -1 && successMessageRef?.current) {
            successMessageRef.current.scrollIntoView({behavior: "smooth"});
        }
    }, [drivers]);

    const setAccessTicketItemsHandler = (items: PackagesCartItem[]) => {
        return items
            .filter((item: PackagesCartItem) => {
                if (!item.packageItem) {
                    return false;
                }
                return item.packageItem.type === PackageItemItemType.PackageItem;
            })
            .filter((item: PackagesCartItem) => {
                if (!item.packageItem) {
                    return false;
                }
                return item.packageItem.article.ticket_type === ArticleTicketType.Access;
            });
    };

    useEffect(() => {
        if (selectedReservation && selectedReservation.packagesCart) {
            setPackagesCart(selectedReservation.packagesCart);
            setAccessTicketItems(setAccessTicketItemsHandler(selectedReservation.packagesCart?.packagesCartItems ?? []));
        }
    }, [selectedReservation]);

    const dispatchAsync = action => {
        return new Promise(resolve => {
            dispatch(action);
            resolve(null);
        });
    };

    const resetFilters = async () => {
        try {
            await Promise.all([dispatchAsync(BookingAction.clearBookingProcess()), dispatchAsync(BookingAction.resetSelectedPackageItems())]);

            await Promise.all([
                dispatchAsync(
                    BookingAction.setFilter({
                        ...bookingProcess.filter,
                        activity_ids: firstTimeslot && firstTimeslot?.activity_id ? [firstTimeslot.activity_id] : [1],
                    })
                ),
            ]);
        } catch (error) {
            consoleLog("Error reset filters", error);
        }
    };

    const onClickGoToUpsells = async () => {
        await resetFilters();
        if (!selectedReservation) {
            console.log("selectedReservation is null");
            return;
        }
        return history.push("/booking/login/reservations/detail/" + reservation_id + "/upsells/" + selectedReservation.packagesCart?.guid);
    };

    const updateIsSessionDriverAssigned = () => {
        return profile == null ? false : drivers.some(resDriver => resDriver.driver_id == profile.id);
    };

    const bookingLoginReservationDetailDriverRedesignContextValue: ReservationDetailContextType = {
        driversInReservation: drivers,
        newDriver: true,
        isSessionDriverAssigned: isSessionDriverAssigned,
        profileDriver: profile,
        saveDriver: () => {},
        setShowDetail: value => {},
    };

    const scrollToTop = () => {
        console.log("scrollToTop");
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    useEffect(() => {
        console.log(location.pathname);
        scrollToTop();
    }, [location]);

    useEffect(() => {
        setIsSessionDriverAssigned(updateIsSessionDriverAssigned());
    }, [profile, selectedReservation, selectedReservation?.packagesCart?.packagesCartItems]);

    useEffect(() => {
        fetchReservationHandler();
    }, []);

    useEffect(() => {
        if (!selectedReservation) {
            return;
        }
        console.log(reservation_id, selectedReservation.id);
        if (selectedReservation.id != reservation_id) {
            setIsLoading(true);
        }
    }, [reservation_id, selectedReservation]);

    if (!selectedReservation || selectedReservation.id != reservation_id) {
        return (
            <div className={"BookingLoginReservationDetailRedesignWithoutReservation w-full flex flex-col justify-center"}>
                <div className={"w-full flex flex-col"}>
                    <div className={"flex flex-col gap-y-[20px] md:gap-y-[55px]"}>
                        <div className={"bg-arbo-blue bg-arbo-green text-arbo-blue text-arbo-green hidden"}></div>
                        <div className={"w-full text-left"}>
                            <BookingLoginZuumeHeader title={t("Your booking")} />
                        </div>

                        <div className={"flex flex-row flex-nowrap gap-x-3 md:gap-x-6"}>
                            <BookingLoginReservationInfoCard reservation={null} />
                            <BookingLoginBuyUpsellsCard onClick={onClickGoToUpsells} />
                        </div>
                    </div>
                    <div className={"flex flex-col gap-y-[22px] md:gap-y-[31px] mt-[43px] md:mt-[68px]"}>
                        <div className={"w-full text-left"}>
                            <BookingLoginZuumeHeader title={t("Online checkin")} />
                        </div>

                        <ReservationDataCollectionInfoCard />
                    </div>
                </div>

                {isLoading ? (
                    <div
                        className={
                            "AccessTicketReservationDriversContainer -mx-4 md:mx-0 w-auto min-w-full text-left mt-[36px] md:mt-[73px] mb-2 md:mb-16 flex flex-col md:gap-y-5"
                        }
                    >
                        <div className="AccessTicketReservationDriverItem bg-white md:rounded-[10px] overflow-hidden border-t md:border-0 border-gray-200 shadow-card">
                            <section
                                className="AccessTicketHeader flex flex-row justify-between items-stretch min-h-[36px] bg-gray-50 text-black hover:bg-gray-100 active:focus:bg-gray-100 highlight- cursor-pointer w-full"
                                aria-label="Access ticket driver"
                                aria-expanded="false"
                                aria-controls="AccessTicketDriversAssign"
                            >
                                <div className="AccessTicketPos inline-flex items-center justify-center text-xs md:text-xl px-3 py-1.5 font-semibold md:font-medium w-[40px] md:w-[50px]  text-white shrink-0 overflow-hidden rounded-br-[5px] md:rounded-br-[10px] bg-arbo-blue">
                                    1
                                </div>
                                <div className="AccessTicketName font-semibold md:font-medium text-xs md:text-xl text-left flex flex-row items-center justify-start gap-x-2 md:gap-x-4 grow pl-[11px] md:pl-[31px]">
                                    <div className="AccessTicketNameText flex flex-col justify-center">
                                        <span>Ticket</span>
                                    </div>
                                    <span className="AccessTicketPersonType OnlyShowOnChilren font-light text-xxs md:text-sm pl-1 pr-2 grow text-right place-content-end self-center"></span>
                                </div>
                                <div className="AccessTicketAction flex items-center justify-end">
                                    <div className="px-[12px] md:px-[16px] py-[10px] text-black">
                                        <span aria-hidden="true">
                                            <FeatherIcon className={"size-[20px] md:size-[30px]"} name={"edit"} strokeWidth={2} />
                                        </span>
                                    </div>
                                </div>
                            </section>
                            <div className="AccessTicketDriversAssignationStatus relative">
                                <div className="AccessTicketProfilePicture absolute top-0 bottom-0 left-[8px] md:left-[10px] flex flex-row items-center justify-center text-arbo-blue">
                                    <FeatherIcon className={"size-[20px] md:size-[30px]"} name={"user"} strokeWidth={2} />
                                </div>
                                <div className="reservationDriverEmpty flex flex-col items-stretch justify-center w-full pl-[51px] md:pl-[81px] pr-[12px] md:pr-[16px] pt-[27px] md:pt-[33px] pb-[27px] md:pb-[33px]">
                                    <div className="AccessTicketDriversAssignationStatusItem flex flex-row items-center justify-between gap-x-1.5">
                                        <div className="AccessTicketOptionStatusPill text-black text-xxs md:text-base flex flex-col items-center rounded-[5px] shrink-0 py-[4px] md:py-[5px] px-[10px] overflow-hidden bg-arbo-amber/50">
                                            <span className="font-normal">Ticket zuweisen oder Gast einladen</span>
                                        </div>
                                        <div className="grow-0 flex flex-row gap-x-3 items-center justify-end">
                                            <div className="AccessTicketDriversAssignationStatusCheck basis-[80px] flex justify-center">
                                                <FeatherIcon className={"size-[20px] md:size-[30px] text-arbo-yellow"} name={"clock"} strokeWidth={2} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <CardComponent>
                        <div className={"BookingLoginReservationDetailNoContent w-full p-4"}>
                            <div className={"w-full text-left space-y-2"}>
                                <h1 className={"text-gray-900 text-2xl font-bold mb-0.5"}>{t("Reservation not found")}</h1>
                                <p className={"inline-flex items-center font-semibold text-gray-600 mb-4"}>
                                    <ExclamationTriangleIcon className={"text-red-500 w-7 h-7 mr-4"} />
                                    <span>{t("Please go back to previous page")}</span>
                                </p>
                            </div>
                        </div>
                    </CardComponent>
                )}
            </div>
        );
    }

    return (
        <BookingLoginReservationDetailDriverRedesignContext.Provider value={bookingLoginReservationDetailDriverRedesignContextValue}>
            <div className={"BookingLoginReservationDetail w-full flex flex-col"}>
                <div className={"flex flex-col gap-y-[20px] md:gap-y-[55px]"}>
                    <div className={"bg-arbo-blue bg-arbo-green text-arbo-blue text-arbo-green hidden"}></div>
                    <div className={"w-full text-left"}>
                        <BookingLoginZuumeHeader title={t("Your booking")} />
                    </div>

                    <div className={"flex flex-row flex-nowrap gap-x-3 md:gap-x-6"}>
                        <BookingLoginReservationInfoCard reservation={selectedReservation} />
                        <BookingLoginBuyUpsellsCard onClick={onClickGoToUpsells} />
                    </div>
                </div>
                <div className={"flex flex-col gap-y-[22px] md:gap-y-[31px] mt-[43px] md:mt-[68px]"}>
                    <div className={"w-full text-left"}>
                        <BookingLoginZuumeHeader title={t("Online checkin")} />
                    </div>

                    <ReservationDataCollectionInfoCard />
                </div>
                {packagesCart && packagesCart.packagesCartItems.length > 0 && (
                    <div
                        className={
                            "AccessTicketItemsContainer -mx-4 md:mx-0 w-auto min-w-full text-left mt-[36px] md:mt-[73px] mb-2 md:mb-16 flex flex-col md:gap-y-5"
                        }
                    >
                        {accessTicketItems.length > 0 &&
                            accessTicketItems.map((item, i) => <AccessTicketReservationDriverItem key={item.id} i={i + 1} item={item} />)}
                    </div>
                )}
            </div>
        </BookingLoginReservationDetailDriverRedesignContext.Provider>
    );
};

export default withRouter(BookingLoginReservationDetailRedesign);
